'use client'

import useControlled from '@/hooks/use-controlled'
import generateClassNames from '@/utils/generate-class-names'
import getReactNodeRef from '@/utils/get-react-node-ref'
import type { Placement } from '@floating-ui/react'
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react'
import { cloneElement, forwardRef, isValidElement, useRef } from 'react'
import './tooltip.scss'

const classes = generateClassNames('tooltip', ['inner'])

const ARROW_WIDTH = 12
const ARROW_HEIGHT = 6

export interface TooltipProps
  extends Omit<React.HTMLProps<HTMLElement>, 'title'> {
  children?: React.ReactElement<HTMLElement>
  disabled?: boolean
  placement?: Placement
  show?: boolean
  title?: React.ReactNode
}

const Tooltip = forwardRef<HTMLElement, TooltipProps>(
  function Tooltip(props, forwardedRef) {
    const {
      children,
      disabled,
      placement = 'bottom',
      show: showProp,
      title,
      ...other
    } = props

    const [isOpen, setIsOpen] = useControlled({
      value: showProp,
      defaultValue: false,
    })

    const arrowRef = useRef(null)

    const { refs, floatingStyles, middlewareData, context } = useFloating({
      placement,
      open: isOpen && !disabled,
      onOpenChange: setIsOpen,
      whileElementsMounted: autoUpdate,
      middleware: [
        offset(10),
        flip({
          crossAxis: placement.includes('-'),
          fallbackAxisSideDirection: 'start',
          padding: 10,
        }),
        shift({ padding: 10 }),
        arrow({ element: arrowRef }),
      ],
    })

    const hover = useHover(context, {
      move: false,
      enabled: showProp === undefined,
      delay: { open: 300 },
    })

    const focus = useFocus(context, {
      enabled: showProp === undefined,
    })

    const dismiss = useDismiss(context)

    const role = useRole(context, { role: 'tooltip' })

    const { getReferenceProps, getFloatingProps } = useInteractions([
      hover,
      focus,
      dismiss,
      role,
    ])

    const ref = useMergeRefs([
      refs.setReference,
      forwardedRef,
      getReactNodeRef(children),
    ])

    const arrowX = middlewareData.arrow?.x ?? 0
    const arrowY = middlewareData.arrow?.y ?? 0
    const transformX = arrowX + ARROW_WIDTH / 2
    const transformY = arrowY + ARROW_HEIGHT

    const { isMounted, styles } = useTransitionStyles(context, {
      duration: 200,
      initial: {
        opacity: 0,
        transform: 'scale(0.8)',
      },
      close: {
        opacity: 0,
      },
      common: ({ side }) => ({
        transformOrigin: {
          top: `${transformX}px calc(100% + ${ARROW_HEIGHT}px)`,
          bottom: `${transformX}px ${-ARROW_HEIGHT}px`,
          left: `calc(100% + ${ARROW_HEIGHT}px) ${transformY}px`,
          right: `${-ARROW_HEIGHT}px ${transformY}px`,
        }[side],
      }),
    })

    return (
      <>
        {isValidElement(children) &&
          cloneElement(
            children,
            getReferenceProps({
              ref,
              ...other,
            })
          )}

        {isMounted ? (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              className={classes.root}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <div className={classes.inner} style={styles}>
                {title}
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  width={ARROW_WIDTH}
                  height={ARROW_HEIGHT}
                  fill='var(--tooltip-background)'
                  tipRadius={2}
                />
              </div>
            </div>
          </FloatingPortal>
        ) : null}
      </>
    )
  }
)

export default Tooltip
